import {PageItem_ImageTileContentBlock} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/story_page/v1/service.types';
import {useTranslation} from '@aboutyou/i18n';
import {TrackingPromotionCreative} from '@aboutyou/legacy-tracking-sdk';
import {TrackPromotions} from '@aboutyou/tracking';
import {
  ContentBlockImageTile,
  ImageRatio,
  RADIUS,
  SPACING,
  gridCols,
  gridContainer,
  mediaLargeScreens,
  mediaTabletAndUp,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React from 'react';
import {StoryTileOverlay} from './StoryTileOverlay';

/**
 * A single image with headline, subline and CTA overlaid on top of it.
 * Copy sits inside the image frame, unlike `StoryImageText`, which puts copy in
 * its own column next to the media.
 */
export const StoryImageTile: React.FC<{
  content: PageItem_ImageTileContentBlock;
  streamPosition: number;
  testId?: string;
}> = ({content, streamPosition, testId = 'storyImageTile'}) => {
  const {t} = useTranslation();
  const {ratio, images, headline, subline, cta, promotionTracker} = content;

  if (ratio === undefined || images === undefined) return null;

  const image =
    process.env.STACK === 'mobile'
      ? images.mobileDefault
      : images.desktopDefault;

  const aspectRatio: ImageRatio = `${ratio.width}:${ratio.height}`;
  const ctaUrl = cta?.linkTarget?.url;
  // show=false + a link makes the whole tile tappable; show=true renders a button.
  const tileHref = cta && !cta.show && ctaUrl ? ctaUrl : undefined;
  const ctaButton =
    cta?.show && ctaUrl
      ? {text: cta.text || t('Discover more'), url: ctaUrl}
      : undefined;

  const isLandscape = ratio.width > ratio.height;
  const isClickable = Boolean(tileHref || ctaButton);

  return (
    <Row
      data-testid={`${testId}Tracker`}
      promotion={{
        source: TrackingPromotionCreative.GenericPromotion,
        promotionTracker,
      }}
      streamPosition={streamPosition}
      disableClickTracking={!isClickable}
    >
      <Frame data-orientation={isLandscape ? 'landscape' : 'portrait'}>
        <ContentBlockImageTile
          testId={testId}
          href={tileHref}
          image={{
            url: image.url,
            description: image.description,
            aspectRatio,
          }}
        />

        <StoryTileOverlay
          headline={headline}
          subline={subline}
          cta={ctaButton}
          testId={testId}
        />
      </Frame>
    </Row>
  );
};

const Row = styled(TrackPromotions)`
  @media ${mediaTabletAndUp} {
    ${gridContainer}
  }
`;

const Frame = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
  border-radius: ${RADIUS.Sm};
  padding: ${SPACING['X.Xl']} 0;

  @media ${mediaLargeScreens} {
    ${gridCols(6)}
    margin-inline: auto;

    &[data-orientation='landscape'] {
      ${gridCols(8)}
    }
  }
`;
